import {
  Button,
  Card,
  CardContent,
  Flex,
  LoadingOrbit,
  Menu,
  MenuItem,
  MenuTrigger,
  Text,
} from '@chatsdet/ui';
import {
  ArrowsClockwise,
  DotsThreeVertical,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import { DynamicResultRenderer } from '../DynamicResultRenderer';
import {
  type DashboardCard as DashboardCardType,
  useDeleteDashboardCard,
  useRunCardQuery,
} from './useDashboardData';

type Props = {
  card: DashboardCardType;
  dashboardId: string;
  onEdit: (card: DashboardCardType) => void;
};

export function DashboardCardWidget({ card, dashboardId, onEdit }: Props) {
  const query = useRunCardQuery(card.sql);
  const deleteMutation = useDeleteDashboardCard(dashboardId);

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <Flex
        justify="between"
        align="center"
        className="px-4 py-2 border-b border-border drag-handle cursor-move"
      >
        <Text className="font-medium truncate">{card.title}</Text>
        <Flex
          gap="xs"
          align="center"
          className="no-drag"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Refresh"
            onPress={() => query.refetch()}
          >
            <ArrowsClockwise size={14} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Edit"
            onPress={() => onEdit(card)}
          >
            <PencilSimple size={14} />
          </Button>
          <MenuTrigger>
            <Button variant="ghost" size="sm" iconOnly aria-label="More">
              <DotsThreeVertical size={16} />
            </Button>
            <Menu
              onAction={(key) => {
                if (key === 'delete') {
                  if (window.confirm(`Delete card "${card.title}"?`)) {
                    deleteMutation.mutate(card.id);
                  }
                }
              }}
            >
              <MenuItem id="delete">
                <Trash size={14} className="mr-2" /> Delete card
              </MenuItem>
            </Menu>
          </MenuTrigger>
        </Flex>
      </Flex>
      <CardContent className="flex-1 overflow-auto p-4">
        {query.isLoading ? (
          <Flex align="center" justify="center" className="h-full">
            <LoadingOrbit size="sm" />
          </Flex>
        ) : query.data?.ok === false ? (
          <Text className="text-destructive font-mono text-xs whitespace-pre-wrap">
            {query.data.error}
          </Text>
        ) : query.data ? (
          <DynamicResultRenderer
            result={{
              renderType: card.renderType,
              title: card.title,
              xKey: card.xKey ?? undefined,
              yKey: card.yKey ?? undefined,
              nameKey: card.nameKey ?? undefined,
              data: query.data.data,
            }}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
